// @ts-nocheck

import * as R from 'ramda';
import * as React from 'react';
import Link from '../../../../../common/components/core/Link';
import MessageFactory from '../../models/MessageFactory';
import { passesAmpCompatibility } from '../RuleUtils';
import { BANNER_TYPES, CAMPAIGN_RULE_FIELD, NAME_RULE_FIELD } from '../journeyConstants';

const { BANNER_BOTTOM } = BANNER_TYPES;
import type {
  AppStats,
  BannerType,
  Language,
  Rule,
  RuleBranchView,
  RuleQueryData,
  ValidationMessage,
} from '../../../../types/journeyTypes';

/*
 * Intersects view partitions for journey with current rule by id and finds
 * all AMP compatible view templates and whether any of them use AMP.
 *
 * @param {array} branch_views         - View partitions as list with minimal data
 * @param {object} curr_views_for_rule - A dictionary containing all view data for rule
 * @return {array} types               - Array of types as defined by BANNER_TYPES
 * @return {boolean} compatible        - True if and only if there exists one type thats AMP compatible
 */
function _viewPlatformCompatible(
  branch_views: Array<RuleBranchView>,
  curr_views_for_rule: {
    string: RuleBranchView;
  }
): {
  types: Array<BannerType>;
  compatible: boolean;
} {
  const types = branch_views
    .filter((branch_view) => curr_views_for_rule[branch_view.branch_view_id])
    .map((branch_view) => curr_views_for_rule[branch_view.branch_view_id].type);
  // biome-ignore lint/suspicious/noDoubleEquals: ignored using `--suppress`
  const compatible = types.reduce((memo, type) => memo && type == BANNER_BOTTOM, true);
  return {
    types,
    compatible,
  };
}

export function _getAmpCompatibilityMessage(
  branch_views?: Array<RuleBranchView>,
  curr_views_for_rule?: {
    string: RuleBranchView;
  },
  rule_id: string,
  isAmpCompatible: boolean
): ValidationMessage {
  // error handling
  var messageFactory = new MessageFactory('6');

  if (!branch_views || !(branch_views instanceof Array)) {
    return messageFactory.createError('Views list not found or wrong type (branch_views undefined).');
  }

  if (!curr_views_for_rule || typeof curr_views_for_rule !== 'object') {
    return messageFactory.createError('Current views for rule not found or wrong type (curr_views_for_rule undefined).');
  }

  if (branch_views.length !== Object.keys(curr_views_for_rule).length) {
    return messageFactory.createError(
      'View templates from current partitions set does not equal number of views for this rule.'
    );
  }

  // if isAmpCompatible is false, means that it doesn't need any safeguard checks in the first place
  if (!isAmpCompatible) {
    return messageFactory.createValid(rule_id);
  }

  // ensure view to platform compatibility
  const { types, compatible } = _viewPlatformCompatible(branch_views, curr_views_for_rule);

  if (types.length === 0) {
    return messageFactory.createError('Zero matching views found.');
  } else if (compatible) {
    return messageFactory.createValid(rule_id);
  } else {
    return messageFactory.createFailure(rule_id, {
      required: false,
    });
  }
}

const messageGenerators = {
  audienceConfigurationValidation: function (rule: Rule): ValidationMessage {
    if (!rule || !rule[NAME_RULE_FIELD] || !rule[CAMPAIGN_RULE_FIELD]) {
      const textSpan = (
        <span>
          Audience is invalid.{' '}
          <Link to={`/journeys/audiencerule/${rule.id}/audience-editor`} testid="audience-invalid-link">
            Please enter a {!rule || !rule[NAME_RULE_FIELD] ? 'name' : 'campaign'}.
          </Link>
        </span>
      );
      return {
        text: textSpan,
        required: true,
        type: 'error',
        key: '1',
        testid: 'audience-invalid',
      };
    } else {
      return {
        text: 'Audience is valid.',
        required: true,
        type: 'success',
        key: '1',
        testid: 'audience-valid',
      };
    }
  },
  // Ensures audience split percentages across views total to 100
  viewsValidation: function (rule: Rule): ValidationMessage {
    let data;
    const messageFactory = new MessageFactory('2');
    const freqTotal = rule.branch_views
      ? R.reduce(
          (total, view) => {
            return total + parseInt(view.percentage, 10);
          },
          0,
          rule.branch_views
        )
      : 0;

    if (
      !rule.branch_views ||
      !rule.branch_views.length ||
      R.reduce(
        (failed, view) => {
          // if the id is 0 or "0", or if we have already failed...
          // biome-ignore lint/suspicious/noDoubleEquals: ignored using `--suppress`
          return failed || view.branch_view_id == '0';
        },
        false,
        rule.branch_views
      )
    ) {
      const textSpan = (
        <span>
          Views are invalid.{' '}
          <Link to={`/journeys/audiencerule/${rule.id}/configure-creatives`} testid="views-invalid-link">
            Please select a view for audience.
          </Link>
        </span>
      );
      data = {
        text: textSpan,
        required: true,
        type: 'error',
        key: '2',
        testid: 'views-invalid',
      };
    } else if (freqTotal > 100) {
      data = messageFactory.createWarning('View configuration is invalid. Please target up to 100% of your audience.', {
        testid: 'view-config-invalid',
      });
    } else if (freqTotal < 100) {
      data = messageFactory.createWarning(
        `Only ${freqTotal}% of your audience will experience your Journey; the remaining ${
          100 - freqTotal
        }% will experience your default website behavior.`,
        {
          testid: 'only-some-audience',
        }
      );
    } else {
      data = messageFactory.createSuccess('View configuration is valid. 100% of your audience will see a view.', {
        testid: 'view-config-valid',
      });
    }

    return data;
  },

  /*
   * Ensures a journey that is enabled for AMP is able to provide
   * at least one AMP compatible template
   */
  platformValidation: function (rule: Rule): ValidationMessage {
    const isAmpCompatible: boolean = passesAmpCompatibility(rule);
    return _getAmpCompatibilityMessage(rule.branch_views, rule.curr_views_for_rule, rule.id, isAmpCompatible);
  },
  // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  webSdkValidation: function (rule: Rule, app_stats: AppStats | null | undefined): ValidationMessage | null | undefined {
    if (!app_stats) {
      return {
        type: 'warning',
        required: true,
        text: 'The Web SDK integration status could not be determined at this time.',
        key: '3',
        testid: 'web-sdk-undetermined',
      };
    } else {
      if (app_stats.web) {
        return {
          type: 'success',
          required: true,
          text: 'The Web SDK has been integrated.',
          key: '3',
          testid: 'web-sdk-integrated',
        };
      } else {
        const textSpan = (
          <span>
            Web SDK is not integrated. Your Journey will not function unless you{' '}
            <Link href="https://docs.branch.io/pages/web/integrate/" testid="integrate-web-sdk-link">
              integrate the Web SDK
            </Link>{' '}
            on your website.
          </span>
        );
        return {
          type: 'error',
          required: true,
          text: textSpan,
          key: '3',
          testid: 'web-sdk-not-integrated',
        };
      }
    }
  },
  iosSdkValidation: function (
    // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
    rule: Rule,
    app_stats: AppStats | null | undefined,
    query: RuleQueryData | null | undefined
  ): ValidationMessage | null | undefined {
    if (query && query.devices.ios && query.devices.ios.length) {
      if (app_stats && app_stats.ios) {
        return {
          type: 'success',
          text: 'The iOS SDK has been integrated.',
          key: '4',
          testid: 'ios-sdk-integrated',
        };
      } else {
        var textSpan = (
          <span>
            Your Journey targets iOS users but the{' '}
            <Link href="https://docs.branch.io/pages/apps/ios/" testid="ios-sdk-link">
              iOS SDK
            </Link>{' '}
            is not integrated. Attribution data will be missing.
          </span>
        );
        return {
          type: 'warning',
          text: textSpan,
          key: '4',
          testid: 'ios-sdk-not-integrated',
        };
      }
    }
  },
  androidSdkValidation: function (
    // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
    rule: Rule,
    app_stats: AppStats | null | undefined,
    query: RuleQueryData | null | undefined
  ): ValidationMessage | null | undefined {
    if (query && query.devices.android && query.devices.android.length) {
      if (app_stats && app_stats.android) {
        return {
          valid: true,
          text: 'The Android SDK has been integrated.',
          key: '5',
          testid: 'android-sdk-integrated',
        };
      } else {
        var textSpan = (
          <span>
            Your Journey targets Android users but the{' '}
            <Link href="https://docs.branch.io/pages/apps/android/" testid="android-sdk-link">
              Android SDK
            </Link>{' '}
            is not integrated. Attribution data will be missing.
          </span>
        );
        return {
          type: 'warning',
          text: textSpan,
          key: '5',
          testid: 'android-sdk-not-integrated',
        };
      }
    }
  },
};

// Creates an array of applicable generators from the `messageGenerators` object.
export function _listApplicableMessageGenerators(
  isAmpCompatible: boolean
): Array<
  (
    rule: Rule,
    app_stats: AppStats | null | undefined,
    query: RuleQueryData | null | undefined
  ) => ValidationMessage | null | undefined
> {
  return Object.keys(messageGenerators)
    .filter((generatorKey) => {
      // If the platform is not AMP compatible, we need to filter out the
      // platformValidation generator (as it's not relevant in that scenario).
      return isAmpCompatible || generatorKey !== 'platformValidation';
    })
    .map((generatorKey) => messageGenerators[generatorKey]);
}

function _createValidationMessagesForJourney(
  rule: Rule,
  app_stats: AppStats | null | undefined,
  query: RuleQueryData | null | undefined
): Array<ValidationMessage> {
  const isAmpCompatible = passesAmpCompatibility(rule);
  return _listApplicableMessageGenerators(isAmpCompatible).reduce((messages, messageGenerator) => {
    const message = messageGenerator(rule, app_stats, query);

    if (!message) {
      return [...messages];
    }

    return [...messages, message];
  }, []);
}

/*
 * Separates all messages into those w/ the 'required' property, and those w/o
 *
 * @param - same as _createValidationMessagesForJourney ^
 * @return {
 *    required: [...],
 *    recommended: [...]
 * }
 * Same messages returned from _createValidationMessagesForJourney,
 * but separated into two arrays.
 */
export function getRequiredAndRecommendedMessages(
  rule: Rule,
  app_stats: AppStats,
  query: RuleQueryData
): {
  required: Array<ValidationMessage>;
  recommended: Array<ValidationMessage>;
} {
  const messages = _createValidationMessagesForJourney(rule, app_stats, query);

  if (!messages || !messages.length) {
    return {
      required: [],
      recommended: [],
    };
  }

  return {
    required: messages.filter((message) => message && message.required),
    recommended: messages.filter((message) => message && !message.required),
  };
}

export function canJourneyBeStarted(rule: Rule) {
  return R.all(
    (result) => {
      if (result && result.required) {
        // To help with testing needs, we don't want to prevent starting
        // a Journey if the WebSDK is not integrated.
        if (result.key === '3') {
          return true;
        }

        return result.type !== 'error';
      }

      return true;
    },
    _createValidationMessagesForJourney(rule, null, null)
  );
}

/**
 * Checks if the given field has a unique value across all journeys
 * @param  {string}  field
 * @param  {Array}   journeys
 * @param  {string}  journeyId
 * @param  {string}  value
 * @return {boolean} returns true if field is unique and not empty
 */
export function isFieldUnique(field: string, journeys: Array<Rule>, journeyId: string, value: string): boolean {
  if (journeys && journeys.length) {
    return !journeys
      .filter((journey) => journey.id !== journeyId)
      .find((journey) => journey[field] && journey[field].toUpperCase().trim() === (value && value.toUpperCase().trim()));
  }

  return true;
}

/**
 * Checks if title and campaign fields are valid for the given journey
 * @param  {Array}   journeys
 * @param  {object}  journey
 * @return {Boolean} true if title and campaign are not empty and
 * title is unique
 */
export function isJourneyValid(journeys: Array<Rule>, journey: Rule): boolean {
  if (journey[NAME_RULE_FIELD] && journey[CAMPAIGN_RULE_FIELD]) {
    return (
      isFieldUnique(NAME_RULE_FIELD, journeys, journey.id, journey[NAME_RULE_FIELD]) &&
      isNameValid(journey, journey[NAME_RULE_FIELD])
    );
  }

  return false;
}

/**
 * Determines whether the name of a journey should be validated.
 *
 * The name is validated if:
 * - It is a new journey.
 * - The journey was created without special characters.
 *
 * @param {Rule} journey - The journey object to check.
 * @returns {boolean} Returns true if the name should be validated, otherwise false.
 */
export function shouldValidateName(journey: Rule) {
  if (journey.id === 'new') return true;

  if (JOURNEY_NAME_VALIDATION_REGEX.test(journey.originalTitle)) return true;

  return false;
}

/**
 * Regular expression used to validate journey names.
 *
 * This regular expression matches strings that do not contain certain characters:
 * - Backslash (\)
 * - Ampersand (&)
 * - Slash (/)
 * - Less than sign (<)
 * - Greater than sign (>)
 * - Pipe (|)
 *
 * @constant {RegExp} JOURNEY_NAME_VALIDATION_REGEX
 */
export const JOURNEY_NAME_VALIDATION_REGEX = /^[^\\&/<>|]+$/;
/**
 * Checks if a journey name is valid.
 *
 * @param {string} journeyName - The journey name to validate.
 * @returns {boolean} Returns true if the journey name is valid, otherwise false.
 */
export function isNameValid(journey: Rule, journeyName: string): boolean {
  if (journeyName !== '') {
    if (shouldValidateName(journey)) {
      return JOURNEY_NAME_VALIDATION_REGEX.test(journeyName);
    }
  }

  return true;
}

/**
 * Returns a boolean indicating if both button texts
 * are non-empty
 * @param {object} language map of text values
 * @return {Boolean}
 */
export const checkButtonTextFilledOut = (language: Language): boolean => {
  return !!language.$journeys_button_get_has_app && !!language.$journeys_button_get_no_app;
};
